import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  Avatar,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Save, Lock, PhotoCamera, Delete, Person } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api, useNotification } from '@viilutalous/common';

export default function UserSettings() {
  const { t } = useTranslation();
  const { showSuccess } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const fileInputRef = useRef(null);

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/user/profile');
      setProfile(response.data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (passwordData.password !== passwordData.password_confirmation) {
      setError(t('settings.password_mismatch'));
      setLoading(false);
      return;
    }

    try {
      await api.put('/user/password', passwordData);

      showSuccess(t('settings.password_success'));
      setPasswordData({
        current_password: '',
        password: '',
        password_confirmation: '',
      });
    } catch (err) {
      setError(err.response?.data?.error || t('settings.password_error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('settings.invalid_image_type'));
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('settings.image_too_large'));
      return;
    }

    setPhotoLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/user/photo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setProfile({ ...profile, photo_url: response.data.photo_url });
      showSuccess(t('settings.photo_uploaded'));
    } catch (err) {
      setError(err.response?.data?.detail || t('settings.photo_upload_error'));
    } finally {
      setPhotoLoading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePhotoDelete = async () => {
    setPhotoLoading(true);
    setError('');

    try {
      await api.delete('/user/photo');

      setProfile({ ...profile, photo_url: null });
      showSuccess(t('settings.photo_deleted'));
    } catch (err) {
      setError(err.response?.data?.detail || t('settings.photo_delete_error'));
    } finally {
      setPhotoLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Person />
        {t('settings.user_settings_title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Profile Photo Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('settings.profile_photo')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box
            sx={{
              position: 'relative',
              cursor: photoLoading ? 'default' : 'pointer',
              '&:hover .photo-overlay': {
                opacity: 1,
              },
              '&:hover .delete-button': {
                opacity: 1,
              },
            }}
            onClick={!photoLoading ? handlePhotoClick : undefined}
          >
            <Avatar
              src={profile?.photo_url}
              sx={{ width: 120, height: 120, fontSize: '3rem' }}
            >
              {profile?.firstname?.[0] || user?.email?.[0]?.toUpperCase()}
            </Avatar>
            {/* Camera overlay */}
            <Box
              className="photo-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: profile?.photo_url ? 0 : 0.7,
                transition: 'opacity 0.2s',
              }}
            >
              <PhotoCamera sx={{ color: 'white', fontSize: 40 }} />
            </Box>
            {/* Loading spinner */}
            {photoLoading && (
              <CircularProgress
                size={120}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  color: 'primary.main',
                }}
              />
            )}
            {/* Delete button */}
            {profile?.photo_url && !photoLoading && (
              <IconButton
                className="delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePhotoDelete();
                }}
                size="small"
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    backgroundColor: 'error.dark',
                  },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handlePhotoUpload}
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
            />
          </Box>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {profile?.firstname} {profile?.lastname}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('settings.click_to_upload')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Password Change Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Lock fontSize="small" />
          {t('settings.change_password')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('settings.password_requirements')}
        </Typography>

        <form onSubmit={handlePasswordSubmit}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                label={t('settings.current_password')}
                name="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={handlePasswordChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.new_password')}
                name="password"
                type="password"
                value={passwordData.password}
                onChange={handlePasswordChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('settings.confirm_password')}
                name="password_confirmation"
                type="password"
                value={passwordData.password_confirmation}
                onChange={handlePasswordChange}
                required
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
            >
              {t('settings.update_password')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
}
